import {
    MenuCompositeData,
    MenuExtraGroupData,
    MenuExtraProductData,
    MenuItemData,
    MenuPlgData,
    MenuPlgSellableData,
    MenuSellableData
} from "../menu/menu-entity.model";
import {BehaviorSubject, Subject} from "rxjs";
import {UUID} from "angular2-uuid";
import {OrderEvents} from "./order-events";
import {MenuEvents} from "../menu/menu-events";

export enum OrderType {
    NONE,
    ORDER_AHEAD,
    ORDER_DELIVERY,
    ORDER_AT_TABLE
}

export namespace OrderType {
    export function toString(orderType: OrderType): string {
        return orderType === OrderType.ORDER_AT_TABLE ? "APP_AT_TABLE"
            : orderType === OrderType.ORDER_AHEAD ? "TAKEAWAY"
                : orderType === OrderType.ORDER_DELIVERY ? "DELIVERY"
                    : undefined;
    }

    export function parse(orderType: string): OrderType {
        return orderType === "APP_AT_TABLE" ? OrderType.ORDER_AT_TABLE
            : orderType === "TAKEAWAY" ? OrderType.ORDER_AHEAD
                : orderType === "DELIVERY" ? OrderType.ORDER_DELIVERY
                    : undefined;
    }
}

export interface DisplayableOrderExtraItem {
    name: string;
    quantity: number;
    isGroup: boolean;
    isVisible: boolean;
    isNested: boolean;
}

export interface DisplayableOrderItem {
    readonly identifier: string;
    name: string;
    quantity: number;
    localTotal: number;
    dealId?: string;
    initiatedTotal: number;
    initiatedExtrasTotal: number;
    orderedExtras: DisplayableOrderExtraItem[];
    compositeOrderItems: DisplayableOrderItem[];
}

export abstract class QuantifiedItem {
    protected _quantity: number;
    private debouncedQuantity$: Subject<number>;

    protected constructor(public parent: OrderItem) {
    }

    public get quantity(): number {
        return this._quantity;
    }

    public set quantity(quantity: number) {
        this._quantity = quantity;

        // Debounce notification of a quantity change
        if (!this.debouncedQuantity$) {
            this.debouncedQuantity$ = new BehaviorSubject<number>(1);
            this.debouncedQuantity$.debounceTime(300).subscribe(() => this.itemChanged());
        }
        this.debouncedQuantity$.next(this._quantity);
    }

    /**
     * Increment the quantity
     * @param qty quantity to be incremented or if is undefined it will increment by 1
     */
    public incrementQuantity(qty?: number): void {
        this.quantity = this.quantity + (qty ? qty : 1);
    }

    /**
     * Decrement the quantity
     * @param qty quantity to be decremented or if is undefined it will decrement by 1
     */
    public decrementQuantity(qty?: number): void {
        let newQuantity = this.quantity - (qty ? qty : 1);
        if (newQuantity >= 0) {
            this.quantity = newQuantity;
        }
    }

    protected itemChanged(notify: boolean = true): void {
        if (this.parent) {
            this.parent.itemChanged(false);
        }
        if (notify) {
            OrderEvents.orderChanged$.next();
        }
    }
}

export class OrderExtraItem extends QuantifiedItem implements DisplayableOrderExtraItem {
    public readonly identifier: string;
    public readonly unitPrice: number;

    public sortingExtraGroupIndex: number;

    constructor(parent: OrderItem,
                public readonly extraGroup: MenuExtraGroupData,
                public readonly extraProduct: MenuExtraProductData,
                quantity: number = 1) {
        super(parent);
        this.identifier = UUID.UUID();
        this.unitPrice = extraProduct.price;
        this._quantity = quantity;
    }

    public get total(): number {
        return this._quantity * this.unitPrice;
    }

    public get isGroup(): boolean {
        return MenuEvents.menuModel.value.extraGroupIsSingleSelect(this.extraGroup);
    }

    public get isVisible(): boolean {
        return MenuEvents.menuModel.value.extraGroupIsVisible(this.extraGroup);
    }

    public get isNested(): boolean {
        return !!MenuEvents.menuModel.value.getParentExtraGroupId(this.extraGroup, this.parent.sellable);
    }

    public get groupName(): string {
        return this.extraGroup.name;
    }

    public get name(): string {
        if (this.extraProduct.menuItem) {
            return this.extraProduct.menuItem.name;
        } else {
            return this.extraProduct.productLine.name;
        }
    }

    public createClone(orderItem: OrderItem): OrderExtraItem {
        let cloned: OrderExtraItem = new OrderExtraItem(orderItem, this.extraGroup, this.extraProduct);
        cloned._quantity = this._quantity;
        return cloned;
    }
}

export class OrderItem extends QuantifiedItem implements DisplayableOrderItem {
    public readonly identifier: string;
    public readonly unitPrice: number;

    public dirty: boolean = false;
    public justOrdered: boolean = false;
    public initiatedTotal: number;
    public initiatedExtrasTotal: number;
    private _totalIncludedSingleSelectExtras: number;
    private _totalIncludedMultiSelectExtras: number;
    private _orderedExtras: OrderExtraItem[] = [];
    protected _localTotal: number = 0;
    public localTotal$: BehaviorSubject<number> = new BehaviorSubject(0);

    /**
     * Maps the ordered extras to an extra group id + sellable id
     */
    private _extraMapByExtraSellableId: Map<string, OrderExtraItem> = new Map();

    /**
     * The group base price for the extras. It can be the cheapest price when there's no default qty or the price for the default qty.
     * Only available when the extra is a group
     */
    private _groupBasePriceMap: Map<number, number>;

    constructor(parent: OrderItem,
                public name: string,
                public menuItem: MenuItemData,
                public sellable: MenuSellableData,
                quantity: number,
                public transferRequestId?: string,
                public transferRequestName?: string,
                public dealId?: string,
                public proposed: boolean = false) {
        super(parent);
        this.identifier = UUID.UUID();
        this.unitPrice = sellable ? sellable.price : undefined;
        this._quantity = quantity || 1;
        if (this.sellable) {
            this.initViewForSellable();
        }
        this.calculateTotals();
    }

    public get totalIncludedMultiSelectExtras(): number {
        return this._totalIncludedMultiSelectExtras;
    }

    public get totalIncludedSingleSelectExtras(): number {
        return this._totalIncludedSingleSelectExtras;
    }

    public get localTotal(): number {
        return this._localTotal;
    }

    public get orderedExtras(): OrderExtraItem[] {
        return this._orderedExtras.slice();
    }

    public getOptionalOrderedExtrasForGroups(extraGroups: MenuExtraGroupData[]): OrderExtraItem[] {
        const optionalExtraGroupIds: number[] = extraGroups.filter((extraGroup: MenuExtraGroupData) => !extraGroup.groupQtyMin).map((extraGroup: MenuExtraGroupData) => extraGroup.id);
        return this._orderedExtras.filter((item: OrderExtraItem) => {
            if (extraGroups.indexOf(item.extraGroup) < 0) {
                return false;
            }
            const parentExtraGroupId: number = MenuEvents.menuModel.value.getParentExtraGroupId(item.extraGroup, this.sellable);
            return parentExtraGroupId ? optionalExtraGroupIds.indexOf(parentExtraGroupId) >= 0 : !item.extraGroup.groupQtyMin;
        });
    }

    public get price(): number {
        return this.sellable.price;
    }

    public get availableExtraGroups(): MenuExtraGroupData[] {
        return this.sellable ? this.sellable.extras : [];
    }

    public get compositeOrderItems(): DisplayableOrderItem[] {
        return null;
    }

    public get selectedFilterTags(): string {
        let tags: string = "";
        this._orderedExtras.forEach((extraItem: OrderExtraItem) => {
            if (extraItem.extraProduct.menuItem && extraItem.extraProduct.menuItem.tagItemOnOptionSelection && extraItem.extraProduct.menuItem.filterTags) {
                tags += " " + extraItem.extraProduct.menuItem.filterTags;
            }
        });
        return tags;
    }

    public addToList(orderItems: OrderItem[], index?: number) {
        if (index === undefined) {
            for (let i: number = orderItems.length - 1; i >= 0; i--) {
                if (orderItems[i].sellable.sellableId === this.sellable.sellableId) {
                    index = i + 1;
                    break;
                }
            }
        }
        if (index === undefined) {
            orderItems.push(this);
        } else {
            orderItems.splice(index, 0, this);
        }
    }

    protected initViewForSellable(): void {
        const availableSingleSelectExtraGroups: MenuExtraGroupData[] = this.sellable.extras.filter((extraGroup: MenuExtraGroupData) => MenuEvents.menuModel.value.extraGroupIsSingleSelect(extraGroup));
        const availableMultiSelectExtraGroups: MenuExtraGroupData[] = this.sellable.extras.filter((extraGroup: MenuExtraGroupData) => !MenuEvents.menuModel.value.extraGroupIsSingleSelect(extraGroup));

        this._totalIncludedSingleSelectExtras = availableSingleSelectExtraGroups.reduce((total: number, extraGroup: MenuExtraGroupData) => total + (extraGroup.totalIncluded || 0), 0);
        this._totalIncludedMultiSelectExtras = availableMultiSelectExtraGroups.reduce((total: number, extraGroup: MenuExtraGroupData) => total + (extraGroup.totalIncluded || 0), 0);

        // calculate the base prices for the single select extras
        this._groupBasePriceMap = new Map();
        availableSingleSelectExtraGroups.forEach((extraGroup: MenuExtraGroupData) => {
            let value: number = 0;
            extraGroup.extraProducts.every((extraProduct: MenuExtraProductData) => {
                if (extraProduct.defaultOrderQty) {
                    value = extraProduct.price;
                    return false;
                }
            });
            this._groupBasePriceMap.set(extraGroup.id, value);
        });
    }

    public sellableQtyOrdered(qty: number, sellableId: number): number {
        let qtyOrdered: number = 0;
        if (this.sellable && this.sellable.sellableId === sellableId) {
            qtyOrdered += qty;
        }
        qtyOrdered += this.orderedExtras
            .filter((extra: OrderExtraItem) => extra.extraProduct.productLine.sellableId === sellableId)
            .reduce((total: number, extra: OrderExtraItem) => total + (qty * extra.quantity), 0);
        return qtyOrdered;
    }

    public accumulateSellableQtyOrdered(qty: number, qtyOrderedBySellableId: { [sellableId: number]: number }): void {
        if (this.sellable) {
            qtyOrderedBySellableId[this.sellable.sellableId] = (qtyOrderedBySellableId[this.sellable.sellableId] || 0) + qty;
        }
        this.orderedExtras.forEach((extra: OrderExtraItem) => qtyOrderedBySellableId[extra.extraProduct.productLine.sellableId] = (qtyOrderedBySellableId[extra.extraProduct.productLine.sellableId] || 0) + (qty * extra.quantity));
    }

    /**
     * Add all default extras for the item.
     * For extras with assigned menu items, the available qty (if specified) is enforced.
     * For the purpose of calculating the quantity already used in the order, this order item (and its parent composite item if any)
     * is/are assumed not to have been added to the order at this time.
     *
     * @param orderCompositeItem  If the item is part of a composite - used for checking available quantities - otherwise this order item is used.
     * @param limitToProducts  If specified, only check default for products in this list.
     * @param excludeExtraGroups  If specified, do not add defaults for extra groups in this list.
     * @return true if any changes are made, false otherwise
     */
    public addDefaultExtras(orderCompositeItem?: OrderCompositeItem, limitToProducts?: MenuExtraProductData[], excludeExtraGroups?: MenuExtraGroupData[]): boolean {
        let qtyOrderedBySellableId: { [sellableId: number]: number } = undefined;
        let changed: boolean = false;
        this.availableExtraGroups.forEach((extraGroup: MenuExtraGroupData) => extraGroup.extraProducts.forEach((extraProduct: MenuExtraProductData) => {
            if (excludeExtraGroups && excludeExtraGroups.some((excludedGroup: MenuExtraGroupData) => excludedGroup.id === extraGroup.id)) {
                return;
            }
            if (extraProduct.defaultOrderQty &&
                (!limitToProducts || limitToProducts.indexOf(extraProduct) >= 0) &&
                this._orderedExtras.find((orderedExtra: OrderExtraItem) => orderedExtra.extraProduct === extraProduct) == undefined &&
                this.isExtraProductAvailable(extraProduct, orderCompositeItem ? orderCompositeItem.menuItem : this.menuItem) &&
                this.isExtraGroupConditionallyAvailable(extraGroup)) {
                let qtyToOrder: number = extraProduct.defaultOrderQty;

                // Don't add defaults in excess of group limit
                const groupQtyLimit: number = MenuEvents.menuModel.value.getExtraGroupEffectiveQtyLimit(extraGroup);
                if (groupQtyLimit) {
                    qtyToOrder = Math.min(qtyToOrder, Math.max(groupQtyLimit - this.getExtraGroupCount(extraGroup), 0));
                }

                // Check available quantity
                const qtyAvailable: number = MenuEvents.menuModel.value.sellableQtyAvailable(extraProduct.productLine.sellableId);
                if (extraProduct.productLine && qtyAvailable != undefined) {

                    // Determine current quantities of all products in the order
                    if (!qtyOrderedBySellableId) {
                        qtyOrderedBySellableId = OrderEvents.orderModel.value.accumulateSellableQtyOrdered(orderCompositeItem || this);
                    }
                    const qtyOrdered = qtyOrderedBySellableId[extraProduct.productLine.sellableId] || 0;

                    qtyToOrder = Math.min(qtyToOrder, qtyAvailable - qtyOrdered);
                }

                if (qtyToOrder > 0) {
                    let orderExtraItem: OrderExtraItem = this.createOrderExtraItem(extraGroup, extraProduct, qtyToOrder);
                    this.addOrderExtraItem(orderExtraItem);
                    changed = true;
                }
            }
        }));
        if (changed) {
            this.itemChanged();
        }
        return changed;
    }

    protected calculateTotals(): void {
        this._localTotal = (this._quantity * this.unitPrice) + this.calculateTotalExtras();
        this.localTotal$.next(this._localTotal);
    }

    public calculateTotalExtras(unit: boolean = false): number {
        let totalIncluded: number = this._totalIncludedSingleSelectExtras + this._totalIncludedMultiSelectExtras;
        return ((unit ? 1 : this._quantity) *
            this._orderedExtras.reduce((total: number, orderExtraItem: OrderExtraItem) => {
                let totalExtra: number = orderExtraItem.total;
                if (totalIncluded && orderExtraItem.extraGroup.totalIncluded) {
                    if (totalIncluded >= totalExtra) {
                        totalIncluded -= totalExtra;
                        totalExtra = 0;
                    } else {
                        totalExtra -= totalIncluded;
                        totalIncluded = 0;
                    }
                }
                return total + totalExtra;
            }, 0));
    }

    public getOrderedExtraByExtraSellableId(extraId: number, sellableId: number): OrderExtraItem {
        return this._extraMapByExtraSellableId.get(extraId + "_" + sellableId);
    }

    /**
     * Determines whether the given extra product is available generally, and specifically for this order item and the specified menu item.
     * Existing filter tags from existing options on this order item are applied to filter conditional extras.
     *
     * @param extraProduct  The extra product to check
     * @param productMenuItem  The menu item being used to order the product. Used for extras filtering. For composites, this should be the composite product's menu item rather than that of a component.
     */
    public isExtraProductAvailable(extraProduct: MenuExtraProductData, productMenuItem: MenuItemData): boolean {
        if (extraProduct && extraProduct.productLine && extraProduct.productLine.suppressForDecomposedProductLine && this.sellable && this.sellable.decomposedProductLine) {
            return false;
        }
        return MenuEvents.menuModel.value.extraItemIsAvailable(extraProduct ? extraProduct.menuItem : undefined, productMenuItem, this.selectedFilterTags);
    }

    public getBasePriceForGroup(extraId: number): number {
        return this._groupBasePriceMap.get(extraId);
    }

    /**
     * Returns a list of orderedExtras with quantity that are below the quantity minimum
     */
    public getExtraGroupsBelowQuantityMinimum(): MenuExtraGroupData[] {
        let missingExtraGroups: MenuExtraGroupData[] = [];
        this.sellable.extras.filter((extraGroup: MenuExtraGroupData) => extraGroup.groupQtyMin).forEach((extraGroup: MenuExtraGroupData) => {
            if (this.isExtraGroupConditionallyAvailable(extraGroup) && this.isExtraGroupBelowQuantityMinimum(extraGroup)) {
                missingExtraGroups.push(extraGroup);
            }
        });
        return missingExtraGroups;
    }

    public isExtraGroupConditionallyAvailable(extraGroup: MenuExtraGroupData): boolean {
        return !MenuEvents.menuModel.value.getParentExtraGroupId(extraGroup, this.sellable) ||
            this.orderedExtras.some((extraItem: OrderExtraItem) => extraGroup.parentExtraProductIds.indexOf(extraItem.extraProduct.id) >= 0);
    }

    public isExtraGroupBelowQuantityMinimum(extraGroup: MenuExtraGroupData): boolean {
        if (extraGroup.groupQtyMin) {
            const totalQuantity: number = this.orderedExtras.filter((orderExtraItem: OrderExtraItem) => orderExtraItem.extraGroup.id === extraGroup.id)
                .reduce((total, item) => total + item.quantity, 0);
            return totalQuantity < extraGroup.groupQtyMin;
        } else {
            return false;
        }
    }

    public getExtraGroupCount(extraGroup: MenuExtraGroupData): number {
        return this.orderedExtras.filter((orderExtraItem) => orderExtraItem.extraGroup.id === extraGroup.id).reduce((total: number, orderExtraItem: OrderExtraItem) => total + orderExtraItem.quantity, 0);
    }

    public hasExtraGroupReachedMin(extraGroup: MenuExtraGroupData): boolean {
        return (extraGroup.groupQtyMin ? this.getExtraGroupCount(extraGroup) <= extraGroup.groupQtyMin : false);
    }

    public hasExtraGroupReachedLimit(extraGroup: MenuExtraGroupData): boolean {
        const groupQtyLimit: number = MenuEvents.menuModel.value.getExtraGroupEffectiveQtyLimit(extraGroup);
        return groupQtyLimit != null && this.getExtraGroupCount(extraGroup) >= groupQtyLimit;
    }

    protected clearOrderedExtras(): boolean {
        const changed: boolean = !!this._orderedExtras.length;
        this._orderedExtras.map((extra: OrderExtraItem) => extra.identifier).forEach((identifier: string) => this.removeOrderExtraItem(identifier));
        return changed;
    }

    public createOrderExtraItem(extraGroup: MenuExtraGroupData, extraProductData: MenuExtraProductData, quantity?: number): OrderExtraItem {
        return new OrderExtraItem(this, extraGroup, extraProductData, quantity);
    }

    public addOrderExtraItem(orderExtraItem: OrderExtraItem, notify: boolean = false): void {
        const sortingExtraGroupId: number = orderExtraItem.isNested ? MenuEvents.menuModel.value.getParentExtraGroupId(orderExtraItem.extraGroup, this.sellable) : orderExtraItem.extraGroup.id;
        orderExtraItem.sortingExtraGroupIndex = this.sellable.extras.findIndex((extraGroup: MenuExtraGroupData) => extraGroup.id === sortingExtraGroupId);
        this._orderedExtras.push(orderExtraItem);
        this.sortExtraItems();

        this._extraMapByExtraSellableId.set(orderExtraItem.extraGroup.id + "_" + orderExtraItem.extraProduct.productLine.sellableId, orderExtraItem);
        this.itemChanged(notify);
    }

    public removeOrderExtraItem(identifier: string, notify: boolean = false): boolean {
        let index: number = this._orderedExtras.findIndex((extra: OrderExtraItem) => extra.identifier === identifier);
        if (index >= 0) {
            this._extraMapByExtraSellableId.delete(this._orderedExtras[index].extraGroup.id + "_" + this._orderedExtras[index].extraProduct.productLine.sellableId);
            this._orderedExtras.splice(index, 1);
            this.sortExtraItems();
            this.itemChanged(notify);
            return true;
        }
        return false;
    }

    /**
     * Sort extras by the order of the extra group, and product within the group as received from the server.
     * Nested extras are sorted within their parent group, directly after the first extra product that references their group.
     */
    private sortExtraItems() {
        this._orderedExtras.sort((a: OrderExtraItem, b: OrderExtraItem) => {
            const sortingExtraProductIdA: string = a.isNested ? (this.firstNestingProductId(a) + "." + a.extraGroup.id + "." + a.extraProduct.id) : a.extraProduct.id;
            const sortingExtraProductIdB: string = b.isNested ? (this.firstNestingProductId(b) + "." + b.extraGroup.id + "." + b.extraProduct.id) : b.extraProduct.id;
            return a.sortingExtraGroupIndex !== b.sortingExtraGroupIndex ? a.sortingExtraGroupIndex - b.sortingExtraGroupIndex
                : sortingExtraProductIdA.localeCompare(sortingExtraProductIdB);
        });
    }

    /**
     * Return the extra product id of the first ordered extra product which references the group of specified extra item
     * @param orderExtraItem
     */
    private firstNestingProductId(orderExtraItem: OrderExtraItem): string {
        const includingExtraItem: OrderExtraItem = this.orderedExtras.find((item: OrderExtraItem) => orderExtraItem.extraGroup.parentExtraProductIds.indexOf(item.extraProduct.id) >= 0);
        return includingExtraItem ? includingExtraItem.extraProduct.id : "";
    }

    /**
     * Remove an order extra item from the order item by extra group and product
     * @param extraGroup
     * @param extraProduct
     * @param notify
     */
    public removeOrderExtraProduct(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, notify: boolean = false): boolean {
        const orderedExtra: OrderExtraItem = this._orderedExtras.find((orderedExtra: OrderExtraItem) => orderedExtra.extraGroup === extraGroup && orderedExtra.extraProduct === extraProduct);
        return orderedExtra && this.removeOrderExtraItem(orderedExtra.identifier, notify);
    }

    public createClone(): OrderItem {
        const orderItem: OrderItem = new OrderItem(this.parent, this.name, this.menuItem, this.sellable, this.quantity, this.transferRequestId, this.transferRequestName, this.dealId);
        orderItem.dirty = this.dirty;

        this.cloneExtras(orderItem);
        orderItem.itemChanged();
        return orderItem;
    }

    protected cloneExtras(orderItem: OrderItem): void {
        this._orderedExtras.forEach((orderExtraItem: OrderExtraItem) => orderItem.addOrderExtraItem(orderExtraItem.createClone(orderItem)));
    }

    /**
     * Copy the extras from another order item
     * @param orderItem The order item to copy extras from
     * @param orderItemMenuItem Filters out unavailable extras
     * @param clean Remove existing multi-select extras?
     */
    public assignExtras(orderItem: OrderItem, orderItemMenuItem?: MenuItemData, clean: boolean = false): void {
        let applyOrderItem: OrderItem;
        if (orderItem instanceof OrderCompositeItem) {
            applyOrderItem = orderItem.items.find((copyItem) => copyItem.sellable && this.sellable && copyItem.sellable.sellableId === this.sellable.sellableId);
        } else {
            applyOrderItem = orderItem;
        }

        if (applyOrderItem) {
            let changed: boolean = clean && this.clearOrderedExtras();

            applyOrderItem.orderedExtras.forEach((item: OrderExtraItem) => {
                if (orderItemMenuItem && orderItemMenuItem.filterExtras) {
                    if (item.extraProduct.menuItem == undefined ||
                        !MenuEvents.menuModel.value.extraItemIsAvailableForFilters(item.extraProduct.menuItem, orderItemMenuItem, applyOrderItem.selectedFilterTags)) {
                        return;
                    }
                }

                // For single select extra groups, delete all items before adding the copied one, otherwise just delete any extra for the same sellable.
                if (!clean) {
                    this.orderedExtras.forEach((orderedExtra: OrderExtraItem) => {
                        if (orderedExtra.extraGroup.id === item.extraGroup.id &&
                            (MenuEvents.menuModel.value.extraGroupIsSingleSelect(item.extraGroup) || orderedExtra.extraProduct.productLine.sellableId === item.extraProduct.productLine.sellableId)) {
                            this.removeOrderExtraItem(orderedExtra.identifier);
                            changed = true;
                        }
                    });
                }

                if (this.sellable.extras.find((extraGroup: MenuExtraGroupData) => extraGroup.id === item.extraGroup.id)) {
                    this.addOrderExtraItem(item.createClone(this));
                    changed = true;
                }
            });
            if (changed) {
                this.itemChanged();
            }
        }
    }

    public itemChanged(notify: boolean = true) {
        this.calculateTotals();
        this.dirty = true;
        super.itemChanged(notify);
    }
}

export class OrderCompositeItem extends OrderItem {
    constructor(parent: OrderItem,
                name: string,
                menuItem: MenuItemData,
                sellable: MenuSellableData,
                quantity: number,
                public composite: MenuCompositeData,
                public readonly items: OrderItem[],
                transferRequestId?: string,
                transferRequestName?: string,
                dealId?: string,
                proposed: boolean = false) {
        super(parent, name, menuItem, sellable, quantity, transferRequestId, transferRequestName, dealId, proposed);
        items.forEach((item: OrderItem) => item.parent = this);
        this.calculateTotals();
    }

    public get compositeOrderItems(): DisplayableOrderItem[] {
        return this.items;
    }

    public get selectedFilterTags(): string {
        let tags: string = super.selectedFilterTags;
        this.items.forEach((componentItem: OrderItem) => {
            if (componentItem instanceof OrderPlgItem && componentItem.menuItem && componentItem.menuItem.tagItemOnOptionSelection && componentItem.menuItem.filterTags) {
                tags += " " + componentItem.menuItem.filterTags;
            }
        });
        return tags;
    }

    public replaceOrderItem(identifier: string, orderItem: OrderItem): void {
        let index = this.items.findIndex((item: OrderItem) => item.identifier === identifier);
        this.removeOrderItem(identifier);
        this.addOrderItem(orderItem, index);
        this.itemChanged();
    }

    protected addOrderItem(orderItem: OrderItem, index?: number) {
        orderItem.addToList(this.items, index);
    }

    protected removeOrderItem(identifier: string): void {
        this.items.splice(this.items.findIndex((item: OrderItem) => item.identifier === identifier), 1);
    }

    protected calculateTotals(): void {
        super.calculateTotals();
        if (this.items) {
            const totalExtras: number = this.items.reduce((total: number, orderItem: OrderItem) => total + orderItem.calculateTotalExtras(), 0);
            const totalPlg: number = this.items.filter((orderItem: OrderItem) => orderItem instanceof OrderPlgItem).reduce((total: number, orderItem: OrderPlgItem) => {
                return total + (orderItem.sellable ?
                    (orderItem.plg.sellableLines.find((plgSellable: MenuPlgSellableData) => plgSellable.sellable.sellableId === orderItem.sellable.sellableId).additionalPrice || 0)
                    : 0);
            }, 0);
            this._localTotal = this._localTotal + this.quantity * (totalExtras + totalPlg);
            this.localTotal$.next(this._localTotal);
        }
    }

    public createClone(): OrderCompositeItem {
        const compositeItems: OrderItem[] = [];
        this.items.forEach((orderItem: OrderItem) => compositeItems.push(orderItem.createClone()));

        let orderItem: OrderCompositeItem = new OrderCompositeItem(this.parent, this.name, this.menuItem, this.sellable, this.quantity, this.composite, compositeItems);
        orderItem.dealId = this.dealId;
        orderItem.dirty = this.dirty;

        this.cloneExtras(orderItem);
        orderItem.itemChanged();
        return orderItem;
    }

    public assignExtras(orderItem: OrderItem, orderItemMenuItem: MenuItemData): void {
        if (orderItem instanceof OrderCompositeItem) {
            // keep a list of items already applied so when an order item can have the same sellable/plg multiple times
            const applied: OrderItem[] = [];
            orderItem.items.forEach((copyItem: OrderItem) => {
                if (copyItem instanceof OrderPlgItem) {
                    if (copyItem.sellable) {
                        const existingOrderPlgItem: OrderPlgItem = <OrderPlgItem>this.items.find((existingItem: OrderItem) => existingItem instanceof OrderPlgItem
                            && applied.find((appliedItem: OrderItem) => appliedItem === existingItem) == undefined && existingItem.plg.plgId === copyItem.plg.plgId);
                        if (existingOrderPlgItem) {
                            let canSelectSellable = true;
                            if (orderItemMenuItem.filterPlgItems) {
                                const plgSellable: MenuPlgSellableData =
                                    existingOrderPlgItem.plg.sellableLines.find((existingPlgSellable: MenuPlgSellableData) => existingPlgSellable.sellable.sellableId === copyItem.sellable.sellableId);
                                if (plgSellable == undefined || plgSellable.menuItem == undefined ||
                                    !MenuEvents.menuModel.value.plgItemIsAvailableForFilters(plgSellable.menuItem, orderItemMenuItem)) {
                                    canSelectSellable = false;
                                }
                            }
                            if (canSelectSellable) {
                                existingOrderPlgItem.selectSellable(copyItem.sellable, orderItem);
                                existingOrderPlgItem.assignExtras(copyItem, orderItemMenuItem);
                                applied.push(existingOrderPlgItem);
                            }
                        }
                    }
                } else {
                    const existingOrderItem: OrderItem = this.items.find((existingItem) => applied.find((appliedItem: OrderItem) => appliedItem === existingItem) == undefined
                        && existingItem.sellable && copyItem.sellable && existingItem.sellable.sellableId === copyItem.sellable.sellableId);
                    if (existingOrderItem) {
                        existingOrderItem.assignExtras(copyItem, orderItemMenuItem);
                        applied.push(existingOrderItem);
                    }
                }
            });
        } else {
            const existingOrderItem: OrderItem = this.items.find((existingItem) => existingItem.sellable && orderItem.sellable &&
                existingItem.sellable.sellableId === orderItem.sellable.sellableId);
            if (existingOrderItem) {
                existingOrderItem.assignExtras(orderItem, orderItemMenuItem);
            }
        }
    }

    public sellableQtyOrdered(qty: number, sellableId: number): number {
        return this.items.reduce((total: number, child: OrderItem) => total + child.sellableQtyOrdered(qty * child.quantity, sellableId), super.sellableQtyOrdered(qty, sellableId));
    }

    public accumulateSellableQtyOrdered(qty: number, qtyOrderedBySellableId: { [sellableId: number]: number }): void {
        super.accumulateSellableQtyOrdered(qty, qtyOrderedBySellableId);
        this.items.forEach((child: OrderItem) => child.accumulateSellableQtyOrdered(qty * child.quantity, qtyOrderedBySellableId));
    }
}

export class OrderPlgItem extends OrderItem {
    constructor(parent: OrderItem,
                name: string,
                sellable: MenuSellableData,
                quantity: number,
                public plg: MenuPlgData,
                transferRequestId?: string,
                transferRequestName?: string) {
        super(parent, name, sellable ? sellable.menuItem : undefined, sellable, quantity, transferRequestId, transferRequestName);
    }

    public selectSellable(sellable: MenuSellableData, orderCompositeItem: OrderCompositeItem): void {
        if (!this.sellable || this.sellable.sellableId !== sellable.sellableId) {
            this.sellable = sellable;
            this.menuItem = sellable.menuItem;
            this.name = sellable.menuItem ? sellable.menuItem.name : sellable.name;
            this.initViewForSellable();

            this.clearOrderedExtras();
            this.addDefaultExtras(orderCompositeItem);
            this.itemChanged();
        }
    }

    public createClone(): OrderPlgItem {
        let orderItem: OrderPlgItem = new OrderPlgItem(this.parent, this.name, this.sellable, this.quantity, this.plg);
        orderItem.dealId = this.dealId;
        orderItem.dirty = this.dirty;

        this.cloneExtras(orderItem);
        orderItem.itemChanged();
        return orderItem;
    }
}


// WEBPACK FOOTER //
// ./src/app/order/order.ts